import React, {Suspense} from 'react';
import {Redirect, Route, RouteComponentProps, Switch} from 'react-router';
import MenuSidebar from '../MenuSidebar/MenuSidebar';
import {Button, Dimmer, Loader, Modal, SemanticICONS, Sidebar} from 'semantic-ui-react';
import AppHeader from '../AppHeader/AppHeader';
import routes from '../routes';
import {default as axios} from 'axios';

import '../../../lib/ace-imports';

import './MainLayoutContainer.css';
import authService from '../../Core/AuthService';
import {Entity} from "../../Core/models";
import AppRoute from "../AppRoute";
import RestClient from "../../../lib/RestClient";
import ErrorBoundary from "../../Common/ErrorBoundary/ErrorBoundary";
import entityTypeListObservableStore from "../../Core/entityTypeListObservableStore";

import {WithTranslation, withTranslation} from 'react-i18next';
import {Data} from "rete/types/core/data";
import {Dates, SortDirection, SystemContextI, SystemState} from "../../../casmon/model/DataFlowGraph";
import {ConnectionSystems, PropertiesType} from "../../../casmon/DataFlowGraph/modules/PropertiesModal/PropertiesModal";
import {Transform} from "rete/types/view/area";
import {DFGSystem} from "../../../casmon/model/dto/response/DataFlowGraphRespDTO.interface";

interface MainLayoutContainerState {
    sideBarOpen: boolean;
    isMobile: boolean;
    availableEntities: Array<Entity>;
    routes: Array<AppRoute>;
    sessionExpired: boolean;
    sessionExpiredModalClosed: boolean;
    editorState: EditorStateI,
    systemStates: SystemState[]
}

// TODO: Export in separate file -> for readability
export interface EditorStateI {
    dfgState: DFGStateI | undefined,
    setDfgState: (state: DFGStateI) => void
}

export interface DFGStateI {
    editor: Data,
    editorTransform: Transform,
    dates: Dates,
    modal: Modal | undefined | null,
    enableAutoSave: boolean
}

//TODO: Modal also imported as component -> Names might collide
export interface Modal {
    isOpen: boolean,
    modalType?: PropertiesType,
    system?: DFGSystem | ConnectionSystems,
    height?: number,
    sort?: ModalSort
}

export interface ModalSort {
    column?: string,
    direction?: SortDirection,
    columns?: {
        in: string | undefined,
        out: string | undefined
    },
    directions?: {
        in: SortDirection | undefined,
        out: SortDirection | undefined
    }
}

export const DataFlowContext = React.createContext<EditorStateI | undefined>(undefined);
export const SystemContext = React.createContext<SystemContextI | undefined>(undefined);

class MainLayoutContainer extends React.Component<RouteComponentProps<{}> & WithTranslation, MainLayoutContainerState> {
    private entityListSubscription?: string;
    private defaultRoute: string;
    private axiosInterceptorId?: number;
    private axiosInterceptorIdRestClient?: number;

    constructor(props: RouteComponentProps<{}> & WithTranslation) {
        super(props);
        this.state = {
            sideBarOpen: false,
            isMobile: MainLayoutContainer.isWidthMobile(window.innerWidth),
            availableEntities: [],
            routes: routes,
            sessionExpired: false,
            sessionExpiredModalClosed: false,
            editorState: {
                dfgState: undefined,
                setDfgState: (data: DFGStateI) => this.setEditor(data)
            },
            systemStates: []
        };

        this.defaultRoute = localStorage.getItem('DEBUG_DEFAULT_ROUTE') || '/dataflow';

        this.onResize = this.onResize.bind(this);
        this.toggleSideBar = this.toggleSideBar.bind(this);
        this.closeSideBar = this.closeSideBar.bind(this);
        this.logout = this.logout.bind(this);
        this.setEditor = this.setEditor.bind(this);
        this.setSystemStates = this.setSystemStates.bind(this);
    }

    static isWidthMobile(width: number): boolean {
        return width < 1050;
    }

    setEditor(data: DFGStateI): void {
        this.setState({
          editorState: {
            dfgState: data,
            setDfgState: this.state.editorState.setDfgState
          }
        })
    }

    setSystemStates(newState: SystemState[]): void {
        this.setState({
            systemStates: newState
        });
    }

    onResize() {
        /*
        debounce(() => {
          console.log("Resizing")
          this.setState(prevState => ({
            isMobile: MainLayoutContainer.isWidthMobile(window.innerWidth) !== prevState.isMobile
              ? MainLayoutContainer.isWidthMobile(window.innerWidth)
              : prevState.isMobile
          }));
        },200);
         */
        //FIXME: Triggers whenever browser is changing -> on each pixel -> should only trigger when dragging stops or if mobile tag actually changes
        const newIsMobile = MainLayoutContainer.isWidthMobile(window.innerWidth);
        if (this.state.isMobile !== newIsMobile) {
            this.setState({isMobile: newIsMobile});
        }
    }

    componentDidMount() {

        window.addEventListener('resize', this.onResize);

        const handleError = (error: any) => {
            if (error.response && error.response.status === 401) {
                this.onSessionExpire();
            }
            return Promise.reject(error);
        };
        this.axiosInterceptorId = axios.interceptors.response.use(v => v, handleError);
        this.axiosInterceptorIdRestClient = RestClient.axiosInstance().interceptors.response.use(v => v, handleError);
        this.entityListSubscription = entityTypeListObservableStore.subscribe((_, entities) => {
            if (entities) {
                this.updateAvailableEntities(entities);
            }
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
        if (typeof this.axiosInterceptorId !== 'undefined') {
            axios.interceptors.response.eject(this.axiosInterceptorId);
        }
        if (typeof this.axiosInterceptorIdRestClient !== 'undefined') {
            RestClient.axiosInstance().interceptors.response.eject(this.axiosInterceptorIdRestClient);
        }
        if (this.entityListSubscription) {
            entityTypeListObservableStore.unsubscribe(this.entityListSubscription);
        }
    }

    onSessionExpire() {
        this.setState({
            sessionExpired: true
        });
    }

    toggleSideBar(): void {
        this.setState((prevState) => {
            return {sideBarOpen: !prevState.sideBarOpen};
        });
    }

    closeSideBar(): void {
        this.setState({sideBarOpen: false});
    }

    logout(): void {
        authService.logout().then(() => this.props.history.push('/')).catch((err) => console.error('Logout failed', err));
    }

    updateAvailableEntities(entities: Entity[]) {
        const dataRoute = routes.find(route => route.path === '/data');
        if (dataRoute && entities && Array.isArray(entities)) {
            dataRoute.subRoutes = entities.map(entity => ({
                path: '/data/' + entity.internalName,
                title: entity.displayName,
                icon: entity.icon as SemanticICONS
            }));
        }
        this.setState({routes: routes});
    }


    render() {
        const {t} = this.props;
        return (

            <Sidebar.Pushable>
                {this.state.sessionExpired && !this.state.sessionExpiredModalClosed && (
                    <Modal centered={false} closeOnDimmerClick={false}
                           open={true} size={'small'} closeIcon="close"
                           onClose={() => this.setState({sessionExpiredModalClosed: true})}>
                        <Modal.Header>
                            {t('casmon:main_container.modal_header')}
                        </Modal.Header>
                        <Modal.Content>
                            {t('casmon:main_container.modal_content')}
                        </Modal.Content>
                        <Modal.Actions>
                            <Button primary={true} content={t('casmon:main_container.login_button')}
                                    onClick={() => this.props.history.push('/login?redirectTo=' + encodeURIComponent(this.props.location.pathname))}/>
                        </Modal.Actions>
                    </Modal>
                )}
                <MenuSidebar
                    routes={this.state.routes}
                    open={this.state.sideBarOpen || !this.state.isMobile}
                    onNavigate={this.closeSideBar}
                    currentUser={authService.principal}
                    onLogout={this.logout}
                    currentPath={this.props.location.pathname}
                />
                <Sidebar.Pusher className="app-container">
                    <Dimmer onClick={this.closeSideBar}
                            active={this.state.sideBarOpen && this.state.isMobile}/>
                    <AppHeader
                        routes={routes}
                        onToggleSideBar={this.toggleSideBar}
                        isMobile={this.state.isMobile}
                        onLogout={this.logout}
                    />
                    <SystemContext.Provider
                        value={{systemsState: this.state.systemStates, setSystemsState: this.setSystemStates}}>
                        <DataFlowContext.Provider value={this.state.editorState}>
                            <main className="app-content">
                                <ErrorBoundary errorMessage={t('casmon:main_container.errorMessage')}>
                                    <Suspense fallback={<Loader active={true}/>}>
                                        <Switch>
                                            {routes.filter((route) => !((route.disabled != null && route.disabled()) || (route.requiredRole != null && authService.principal?.roles?.indexOf(route.requiredRole) === -1))).map((route) => {
                                                return (<Route key={route.path} path={route.path}
                                                               component={route.component}/>);
                                            })
                                            }
                                            <Redirect path="/"
                                                      to={this.defaultRoute}/> {/* Default Route - Dashboard */}
                                        </Switch>
                                    </Suspense>
                                </ErrorBoundary>
                            </main>
                        </DataFlowContext.Provider>
                    </SystemContext.Provider>
                </Sidebar.Pusher>

            </Sidebar.Pushable>


        );
    }
}


export default withTranslation()(MainLayoutContainer);
